/**
 * Field Renderer Registry — maps field types to render functions.
 *
 * Extracted from EntityWizardOverlay.js to make field rendering extensible.
 * Built-in field types are pre-registered. Custom field types can be added
 * by plugins or workspace extensions.
 *
 * Usage:
 *   // Register a custom field renderer
 *   fieldRendererRegistry.register('BARCODE_SCANNER', (props) => <BarcodeScanner {...props} />);
 *   fieldRendererRegistry.register('LOCATION_MAP', (props) => <LocationMapField {...props} />);
 *
 *   // Resolve in EntityWizardOverlay
 *   const renderer = fieldRendererRegistry.resolve(field.fieldType);
 *   if (renderer) return renderer(props);
 *
 * Renderer signature:
 *   (props) => React.ReactElement
 *
 *   props: {
 *     field,           — field metadata from entity_field_metadata
 *     value,           — current field value
 *     onChange,         — (fieldName, value) => void
 *     onBlur,          — blur handler (for validation)
 *     error,           — validation error message
 *     isLine,          — is this a detail/line field
 *     isEdit,          — edit mode (vs create)
 *     isDisabled,      — field is not editable
 *     isValidating,    — async validation in progress
 *     colors,          — theme colors
 *     commonSx,        — shared MUI sx styling
 *     dynamicOptions,  — loaded dropdown options cache
 *     resolvedPickers, — resolved picker configs
 *     setPickerField,  — open picker for this field
 *     setPickerOpen,   — toggle picker overlay
 *     FlexService,     — for generate scripts
 *   }
 */

const registry = new Map();

const fieldRendererRegistry = {
  /**
   * Register a field renderer.
   * @param {string} fieldType - Field type (e.g., 'DROPDOWN', 'BARCODE_SCANNER')
   * @param {Function} renderer - (props) => React.ReactElement
   */
  register(fieldType, renderer) {
    if (!fieldType) throw new Error('Field type is required');
    if (typeof renderer !== 'function') throw new Error('Renderer must be a function for: ' + fieldType);
    registry.set(fieldType, renderer);
  },

  /**
   * Resolve a renderer by field type.
   * @param {string} fieldType
   * @returns {Function | null}
   */
  resolve(fieldType) {
    return registry.get(fieldType) || null;
  },

  /**
   * Check if a renderer is registered.
   * @param {string} fieldType
   * @returns {boolean}
   */
  has(fieldType) {
    return registry.has(fieldType);
  },

  /**
   * List all registered field types.
   * @returns {string[]}
   */
  list() {
    return Array.from(registry.keys());
  },

  /**
   * Register multiple renderers at once.
   * @param {Object<string, Function>} renderers
   */
  registerAll(renderers) {
    for (const [type, renderer] of Object.entries(renderers)) {
      this.register(type, renderer);
    }
  },
};

// ========== Built-in Field Renderers ==========
// NOTE: These are NOT pre-registered here because they depend on MUI components
// and internal overlay state (resolvedPickers, dynamicOptions, etc.)
// that are only available inside EntityWizardOverlay.
//
// Instead, EntityWizardOverlay checks the registry FIRST for custom types,
// then falls back to its built-in switch statement for standard types.
//
// This means:
// - DROPDOWN, NUMBER, DATE, DATETIME, TEXTAREA, BOOLEAN, PICKER, TEXT
//   continue to be rendered by EntityWizardOverlay's built-in logic
// - Custom types (BARCODE_SCANNER, LOCATION_MAP, etc.) are resolved
//   from this registry
//
// To register a custom field renderer:
//   import fieldRendererRegistry from './registry/fieldRendererRegistry';
//   fieldRendererRegistry.register('BARCODE_SCANNER', (props) => <MyScanner {...props} />);

export default fieldRendererRegistry;
